import { Link, useLocation } from 'react-router-dom';
import {
  Home, ArrowRightLeft, Settings, Wallet, ReceiptText,
  LayoutDashboard, Briefcase, Target, Landmark, HandCoins,
  TrendingUp, Receipt, Truck, FileText, Users, ShoppingCart,
  Menu, Sparkles, PiggyBank, BarChart3, HelpCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipProvider, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { UserNav } from './user-nav';
import React, { useState, useEffect } from 'react';
import { useActiveWorkspace } from '@/hooks/use-active-workspace';
import { useToast } from '@/hooks/use-toast';
import { Sheet, SheetContent } from './ui/sheet';

// Mobile sidebar context
interface MobileSidebarContextType {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

const MobileSidebarContext = React.createContext<MobileSidebarContextType | null>(null);

export function useMobileSidebar() {
  const context = React.useContext(MobileSidebarContext);
  if (!context) throw new Error('useMobileSidebar must be used within MobileSidebarProvider');
  return context;
}

export function MobileSidebarProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setIsOpen(false); }, [location.pathname]);
  return (
    <MobileSidebarContext.Provider value={{ isOpen, setIsOpen }}>
      {children}
    </MobileSidebarContext.Provider>
  );
}

const personalNav = [
  { href: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { href: '/dashboard/transactions', icon: ArrowRightLeft, label: 'Transactions' },
  { href: '/dashboard/budgets', icon: Target, label: 'Budgets' },
  { href: '/dashboard/savings', icon: PiggyBank, label: 'Savings' },
  { href: '/dashboard/bills', icon: ReceiptText, label: 'Bills' },
  { href: '/dashboard/loans', icon: HandCoins, label: 'Loans' },
  { href: '/dashboard/analytics', icon: BarChart3, label: 'Analytics' },
];

const businessNav = [
  { href: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { href: '/dashboard/business', icon: Briefcase, label: 'Overview' },
  { href: '/dashboard/business-financials', icon: TrendingUp, label: 'Financials' },
  { href: '/dashboard/clients', icon: Users, label: 'Clients' },
  { href: '/dashboard/products', icon: ShoppingCart, label: 'Products' },
  { href: '/dashboard/quotes', icon: FileText, label: 'Quotes' },
  { href: '/dashboard/invoices', icon: FileText, label: 'Invoices' },
  { href: '/dashboard/receipts', icon: Receipt, label: 'Receipts' },
  { href: '/dashboard/delivery-notes', icon: Truck, label: 'Delivery' },
  { href: '/dashboard/analytics', icon: BarChart3, label: 'Analytics' },
];

const bottomNav = [
  { href: '/dashboard/settings', icon: Settings, label: 'Settings' },
  { href: '/dashboard/help', icon: HelpCircle, label: 'Help' },
];

function NavItem({ href, icon: Icon, label, pathname, collapsed }: {
  href: string; icon: any; label: string; pathname: string; collapsed: boolean;
}) {
  const isActive = pathname === href || (href !== '/dashboard' && pathname.startsWith(href));

  const content = (
    <Link
      to={href}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150',
        collapsed ? 'justify-center w-10 h-10 p-0' : 'w-full',
        isActive
          ? 'bg-indigo-600 text-white shadow-md shadow-indigo-200'
          : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
      )}
    >
      <Icon className={cn('flex-shrink-0', collapsed ? 'w-5 h-5' : 'w-4 h-4')} strokeWidth={isActive ? 2.5 : 1.8} />
      {!collapsed && <span className="truncate">{label}</span>}
    </Link>
  );

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent side="right" className="font-medium">{label}</TooltipContent>
      </Tooltip>
    );
  }

  return content;
}

export function Sidebar() {
  const location = useLocation();
  const { activeWorkspace, setActiveWorkspace } = useActiveWorkspace();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebar-collapsed') === 'true'; } catch { return false; }
  });

  const toggleCollapsed = (val: boolean) => {
    setCollapsed(val);
    try {
      localStorage.setItem('sidebar-collapsed', String(val));
      document.documentElement.setAttribute('data-sidebar', val ? 'collapsed' : 'expanded');
    } catch {}
  };

  // Sync on mount
  React.useEffect(() => {
    document.documentElement.setAttribute('data-sidebar', collapsed ? 'collapsed' : 'expanded');
  }, []);

  const navItems = activeWorkspace === 'business' ? businessNav : personalNav;

  return (
    <TooltipProvider delayDuration={0}>
      <aside className={cn(
        'hidden sm:flex flex-col fixed h-full z-40 bg-white border-r border-slate-100 transition-all duration-300',
        collapsed ? 'w-16' : 'w-56'
      )}>
        {/* Logo + collapse toggle */}
        <div className={cn(
          'flex items-center h-16 border-b border-slate-100 px-3',
          collapsed ? 'justify-center' : 'justify-between'
        )}>
          {!collapsed && (
            <Link to="/dashboard" className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-indigo-600 flex items-center justify-center flex-shrink-0">
                <Wallet className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-slate-900 text-sm">KwachaLite</span>
            </Link>
          )}
          {collapsed && (
            <Link to="/dashboard">
              <div className="w-8 h-8 rounded-xl bg-indigo-600 flex items-center justify-center">
                <Wallet className="w-4 h-4 text-white" />
              </div>
            </Link>
          )}
          {!collapsed && (
            <button
              onClick={() => toggleCollapsed(true)}
              className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
            >
              <Menu className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Workspace switcher */}
        {!collapsed && (
          <div className="px-3 py-3 border-b border-slate-100">
            <div className="flex rounded-xl bg-slate-100 p-1 gap-1">
              <button
                onClick={() => setActiveWorkspace('personal')}
                className={cn(
                  'flex-1 text-xs font-semibold py-1.5 rounded-lg transition-all duration-150',
                  activeWorkspace === 'personal'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                )}
              >
                Personal
              </button>
              <button
                onClick={() => setActiveWorkspace('business')}
                className={cn(
                  'flex-1 text-xs font-semibold py-1.5 rounded-lg transition-all duration-150',
                  activeWorkspace === 'business'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                )}
              >
                Business
              </button>
            </div>
          </div>
        )}

        {/* Expand button when collapsed */}
        {collapsed && (
          <div className="px-3 py-2 border-b border-slate-100">
            <button
              onClick={() => toggleCollapsed(false)}
              className="w-10 h-10 rounded-xl flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors mx-auto"
            >
              <Menu className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Main nav */}
        <nav className={cn('flex-1 overflow-y-auto py-3', collapsed ? 'px-3' : 'px-3')}>
          <div className={cn('space-y-0.5', collapsed && 'flex flex-col items-center')}>
            {navItems.map(item => (
              <NavItem
                key={item.href + item.label}
                href={item.href}
                icon={item.icon}
                label={item.label}
                pathname={location.pathname}
                collapsed={collapsed}
              />
            ))}
          </div>
        </nav>

        {/* Bottom nav */}
        <div className={cn('border-t border-slate-100 py-3', collapsed ? 'px-3' : 'px-3')}>
          <div className={cn('space-y-0.5 mb-3', collapsed && 'flex flex-col items-center')}>
            {bottomNav.map(item => (
              <NavItem
                key={item.href}
                href={item.href}
                icon={item.icon}
                label={item.label}
                pathname={location.pathname}
                collapsed={collapsed}
              />
            ))}
          </div>
          <div className={cn('flex', collapsed ? 'justify-center' : 'px-1')}>
            <UserNav />
          </div>
        </div>
      </aside>
    </TooltipProvider>
  );
}

export function MobileNav() {
  const location = useLocation();
  const { activeWorkspace } = useActiveWorkspace();

  const coreItems = activeWorkspace === 'personal'
    ? [
        { href: '/dashboard', icon: Home, label: 'Home' },
        { href: '/dashboard/transactions', icon: ArrowRightLeft, label: 'Transactions' },
        { href: '/dashboard/budgets', icon: Target, label: 'Budgets' },
        { href: '/dashboard/savings', icon: PiggyBank, label: 'Savings' },
        { href: '/dashboard/analytics', icon: BarChart3, label: 'Analytics' },
      ]
    : [
        { href: '/dashboard', icon: Home, label: 'Home' },
        { href: '/dashboard/business', icon: Briefcase, label: 'Business' },
        { href: '/dashboard/clients', icon: Users, label: 'Clients' },
        { href: '/dashboard/invoices', icon: FileText, label: 'Invoices' },
        { href: '/dashboard/analytics', icon: BarChart3, label: 'Analytics' },
      ];

  return (
    <div className="sm:hidden fixed bottom-0 left-0 right-0 z-50">
      <div className="bg-white/95 backdrop-blur-xl border-t border-slate-100 safe-area-bottom">
        <nav className="flex items-center justify-around h-[64px] px-2">
          {coreItems.map(item => {
            const isActive = location.pathname === item.href ||
              (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  'flex flex-col items-center justify-center flex-1 py-1.5 gap-1 rounded-xl transition-all duration-150',
                  isActive ? 'text-indigo-600' : 'text-slate-400'
                )}
              >
                <div className={cn(
                  'w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-150',
                  isActive ? 'bg-indigo-50' : ''
                )}>
                  <item.icon className="w-5 h-5" strokeWidth={isActive ? 2.5 : 1.8} />
                </div>
                <span className={cn('text-[10px] font-semibold', isActive ? 'text-indigo-600' : 'text-slate-400')}>
                  {item.label}
                </span>
              </Link>
            );
          })}
        </nav>
      </div>
    </div>
  );
}

export function MobileSidebar() {
  const { isOpen, setIsOpen } = useMobileSidebar();
  const location = useLocation();
  const { activeWorkspace, setActiveWorkspace } = useActiveWorkspace();

  const navItems = activeWorkspace === 'business' ? businessNav : personalNav;

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent side="left" className="w-72 p-0 flex flex-col bg-white">
        <div className="flex items-center h-16 px-4 border-b border-slate-100">
          <Link to="/dashboard" className="flex items-center gap-2.5" onClick={() => setIsOpen(false)}>
            <div className="w-8 h-8 rounded-xl bg-indigo-600 flex items-center justify-center">
              <Wallet className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-slate-900">KwachaLite</span>
          </Link>
        </div>

        <div className="px-4 py-3 border-b border-slate-100">
          <div className="flex rounded-xl bg-slate-100 p-1 gap-1">
            <button
              onClick={() => setActiveWorkspace('personal')}
              className={cn(
                'flex-1 text-xs font-semibold py-1.5 rounded-lg transition-all',
                activeWorkspace === 'personal' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
              )}
            >Personal</button>
            <button
              onClick={() => setActiveWorkspace('business')}
              className={cn(
                'flex-1 text-xs font-semibold py-1.5 rounded-lg transition-all',
                activeWorkspace === 'business' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
              )}
            >Business</button>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
          {navItems.map(item => {
            const isActive = location.pathname === item.href ||
              (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
            return (
              <Link
                key={item.href + item.label}
                to={item.href}
                onClick={() => setIsOpen(false)}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all',
                  isActive
                    ? 'bg-indigo-600 text-white shadow-md shadow-indigo-200'
                    : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
                )}
              >
                <item.icon className="w-4 h-4 flex-shrink-0" strokeWidth={isActive ? 2.5 : 1.8} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="border-t border-slate-100 p-3 space-y-0.5">
          {bottomNav.map(item => {
            const isActive = location.pathname.startsWith(item.href);
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setIsOpen(false)}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all',
                  isActive ? 'bg-slate-100 text-slate-900' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
                )}
              >
                <item.icon className="w-4 h-4" strokeWidth={1.8} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function MobileSidebarTrigger() {
  const { setIsOpen } = useMobileSidebar();
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => setIsOpen(true)}
      className="h-9 w-9 sm:hidden text-slate-500"
    >
      <Menu className="h-5 w-5" strokeWidth={1.8} />
    </Button>
  );
}
